import React, { useEffect, useMemo, useState } from 'react';
import { Segmented } from '../ui/Segmented.js';
import { Select } from '../ui/Field.js';
import {
  INSTRUMENTS,
  MULTICOURSE_KEY,
  findTuning,
  customTuningFromId,
  CATEGORY_LABELS,
  type Instrument,
  type Tuning,
  TUNINGS,
  groupByCategory,
  tuningsForType,
  typeKeyOf
} from '../../data/tunings.js';
import { useInstrumentChoice, readInstrumentType } from '../../hooks/useGuitarType.js';
import { useCustomTunings, removeCustomTuning } from '../../data/customTunings.js';
import { CustomTuningDialog } from './CustomTuningDialog.js';

const CREATE_VALUE = '__create__';

export interface TuningPickerProps {
  /** Canonical tuning name (or '' for "all" in filter mode, or a legacy/custom string). */
  value: string;
  onChange: (name: string) => void;
  /** Filter mode: adds an "all tunings" option whose value is ''. */
  allowAll?: boolean;
  /** Small hint under the select, e.g. "detected from the file". */
  hint?: string | null;
  /** Option values: the canonical name (stored on tabs, default) or the catalogue id (tuner presets). */
  valueKey?: 'name' | 'id';
  /** Overrides the select's data-testid (the tuner keeps `tuner-preset-select`). */
  selectTestId?: string;
  /** Prefix for data-testid: `${testIdPrefix}-instrument`, `-type`, `-select`. */
  testIdPrefix?: string;
  /** Offer "+ create a custom tuning" (default true; never in filter mode). */
  allowCreate?: boolean;
  className?: string;
}

/** FRE-139: pick guitar or bass, then the string count, then one of the tunings that fit that instrument. */
export const TuningPicker: React.FC<TuningPickerProps> = ({
  value,
  onChange,
  allowAll = false,
  hint,
  testIdPrefix = 'tuning',
  valueKey = 'name',
  selectTestId,
  allowCreate = true,
  className = ''
}) => {
  const [stored, setStored] = useInstrumentChoice();
  const saved = useCustomTunings();
  const [createOpen, setCreateOpen] = useState(false);
  const current: Tuning | undefined =
    valueKey === 'id'
      ? (TUNINGS.find((t) => t.id === value) ?? saved.find((t) => t.id === value) ?? customTuningFromId(value))
      : findTuning(value);
  // The tuning being shown decides the instrument and type (a 7-string file shows 7-string tunings); otherwise the remembered ones.
  const instrument: Instrument = current?.instrument ?? stored.instrument;
  const type = current ? typeKeyOf(current) : stored.type;
  const multi = type === MULTICOURSE_KEY;
  const canCreate = allowCreate && !allowAll && !multi;

  useEffect(() => {
    if (current && (current.instrument !== stored.instrument || typeKeyOf(current) !== stored.type)) {
      setStored(current.instrument, typeKeyOf(current));
    }
  }, [current, stored, setStored]);

  const groups = useMemo(() => groupByCategory(tuningsForType(instrument, type)), [instrument, type]);
  // Saved custom tunings of this type, plus the current value when it is a custom one that is not saved on this device.
  const customOptions = useMemo(() => {
    const list = saved
      .filter((t) => t.instrument === instrument && typeKeyOf(t) === type)
      .map((t) => ({ value: t[valueKey], label: t.name }));
    if (current?.category === 'custom' && !saved.some((t) => t.id === current.id)) list.push({ value, label: current.name });
    return list;
  }, [saved, instrument, type, valueKey, current, value]);
  const isUnknown = Boolean(value) && !current;
  const isSaved = current?.category === 'custom' && saved.some((t) => t.id === current.id);
  const firstOfType = tuningsForType(instrument, type)[0];

  const switchTo = (nextInstrument: Instrument, nextType: string) => {
    setStored(nextInstrument, nextType);
    if (!allowAll || value) {
      // Fall back to the first standard tuning of the new type.
      const first = tuningsForType(nextInstrument, nextType)[0];
      onChange(first ? first[valueKey] : value);
    }
  };

  const types = INSTRUMENTS[instrument].types;
  const stringCount = Number(type) || (current?.strings ?? 6);

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex items-center gap-2">
      <div data-testid={`${testIdPrefix}-instrument`} className="shrink-0">
        <Segmented
          size="sm"
          className="max-lg:[&_[role=tab]]:min-h-[44px] max-lg:[&_[role=tab]]:py-2.5"
          value={instrument}
          onChange={(next) => next !== instrument && switchTo(next as Instrument, readInstrumentType(next as Instrument))}
          options={(Object.keys(INSTRUMENTS) as Instrument[]).map((i) => ({ value: i, label: INSTRUMENTS[i].label }))}
        />
      </div>
      <div data-testid={`${testIdPrefix}-type`} className="min-w-0 overflow-x-auto no-scrollbar [&_[role=tab]]:whitespace-nowrap">
        <Segmented
          size="sm"
          className="max-lg:[&_[role=tab]]:min-h-[44px] max-lg:[&_[role=tab]]:py-2.5"
          value={type}
          onChange={(next) => switchTo(instrument, next)}
          options={types.map((g) => ({ value: g.key, label: g.label, shortLabel: g.shortLabel }))}
        />
      </div>
      </div>
      <Select
        dir="ltr"
        value={value}
        onChange={(e) => {
          if (e.target.value === CREATE_VALUE) setCreateOpen(true);
          else onChange(e.target.value);
        }}
        data-testid={selectTestId ?? `${testIdPrefix}-select`}
        aria-label="کوک"
      >
        {allowAll && <option value="">همه کوک‌ها</option>}
        {isUnknown && <option value={value}>{value}</option>}
        {groups.map((g) => (
          <optgroup key={g.category} label={g.label}>
            {g.tunings.map((t) => (
              <option key={t.id} value={t[valueKey]}>
                {t.name}
              </option>
            ))}
          </optgroup>
        ))}
        {customOptions.length > 0 && (
          <optgroup label={CATEGORY_LABELS.custom}>
            {customOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </optgroup>
        )}
        {canCreate && <option value={CREATE_VALUE}>+ ساخت کوک جدید…</option>}
      </Select>
      {isSaved && current && (
        <button
          type="button"
          className="self-start text-[11px] text-error-soft hover:underline"
          data-testid={`${testIdPrefix}-delete-custom`}
          onClick={() => {
            removeCustomTuning(current.id);
            if (firstOfType) onChange(firstOfType[valueKey]);
          }}
        >
          حذف این کوک
        </button>
      )}
      {canCreate && (
        <CustomTuningDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          instrument={instrument}
          strings={stringCount}
          initialNotes={(current && typeKeyOf(current) === type && current.instrument === instrument ? current : firstOfType)?.notes ?? []}
          onCreated={(t) => onChange(t[valueKey])}
        />
      )}
      {(current?.context || hint) && (
        <p className="text-[11px] leading-5 text-base-content/60" data-testid={`${testIdPrefix}-hint`}>
          {hint && <span className="text-primary-soft">{hint}</span>}
          {hint && current?.context ? ' · ' : ''}
          {current?.context && (
            <span dir="ltr" className="inline-block">
              {current.notes.join(' ')} — {current.context}
            </span>
          )}
        </p>
      )}
    </div>
  );
};
